'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Calendar, Target, Zap, Clock, PlayCircle, CheckCircle2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface CreateSprintSidebarProps {
  isOpen: boolean
  onClose: () => void
  projectId: number | null
  onSuccess?: () => void
}

export function CreateSprintSidebar({ isOpen, onClose, projectId, onSuccess }: CreateSprintSidebarProps) {
  const t = useTranslations('sprintsPage')
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<{
    name: string
    startDate: string
    endDate: string
    status: 'open' | 'active' | 'closed'
    capacity: string
  }>({
    name: '',
    startDate: '',
    endDate: '',
    status: 'open',
    capacity: ''
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!projectId) {
      toast({
        title: 'Error',
        description: 'Missing project information',
        variant: 'destructive'
      })
      return
    }

    if (!formData.name || !formData.startDate || !formData.endDate) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      })
      return
    }

    const startDate = new Date(formData.startDate)
    const endDate = new Date(formData.endDate)

    if (startDate >= endDate) {
      toast({
        title: 'Error',
        description: 'Start date must be before end date',
        variant: 'destructive'
      })
      return
    }

    setLoading(true)

    try {
      const statusMap: Record<string, number> = { open: 0, active: 1, closed: 2 }

      const res = await fetch('/api/db/sprints', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId,
          name: formData.name,
          status: statusMap[formData.status] ?? 0,
          startDate: formData.startDate,
          endDate: formData.endDate,
          sharing: 0
        })
      })

      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.error || 'Failed to create sprint')
      }

      toast({
        title: 'Success',
        description: 'Sprint created successfully'
      })

      // Notify sidebar to refetch sprints
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('sprint-changed'))
      }

      handleClose()
      onSuccess?.()
    } catch (error) {
      console.error('Error creating sprint:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create sprint',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      startDate: '',
      endDate: '',
      status: 'open',
      capacity: ''
    })
    onClose()
  }

  const suggestSprintDates = () => {
    const today = new Date()
    const nextMonday = new Date(today)
    nextMonday.setDate(today.getDate() + ((1 + 7 - today.getDay()) % 7 || 7))

    const twoWeeksLater = new Date(nextMonday)
    twoWeeksLater.setDate(nextMonday.getDate() + 13)

    setFormData({
      ...formData,
      startDate: nextMonday.toISOString().split('T')[0],
      endDate: twoWeeksLater.toISOString().split('T')[0]
    })
  }

  return (
    <GlobalSidebar
      isOpen={isOpen}
      onClose={handleClose}
      title={t('createNewSprint')}
      subtitle={projectId ? `Project ID: ${projectId}` : undefined}
      icon={<Zap className="h-5 w-5" />}
      width="lg"
      footer={
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="create-sprint-form" disabled={loading}>
            {loading ? 'Creating...' : 'Create Sprint'}
          </Button>
        </div>
      }
    >
      <form id="create-sprint-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Sprint Name */}
        <div className="space-y-2">
          <Label htmlFor="sprint-name">
            Sprint Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="sprint-name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Sprint 15"
            required
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="start-date">
              Start Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="start-date"
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="end-date">
              End Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="end-date"
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              required
            />
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={suggestSprintDates}
          className="w-full"
        >
          <Calendar className="h-4 w-4 mr-2" />
          Suggest 2-week Sprint Dates (Next Monday)
        </Button>

        {/* Status and Capacity */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value as 'open' | 'active' | 'closed' })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span>Open</span>
                  </div>
                </SelectItem>
                <SelectItem value="active">
                  <div className="flex items-center gap-2">
                    <PlayCircle className="h-4 w-4 text-green-500" />
                    <span>Active</span>
                  </div>
                </SelectItem>
                <SelectItem value="closed">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-500" />
                    <span>Closed</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="capacity">Capacity (Story Points)</Label>
            <Input
              id="capacity"
              type="number"
              min="0"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
              placeholder="e.g., 40"
            />
          </div>
        </div>

      </form>
    </GlobalSidebar>
  )
}
